import clsx from 'clsx'
import { FieldError, Input, Label } from 'components/core-v2/atoms'
import { noop } from 'lodash'
import { ReactNode } from 'react'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { ClassName } from 'types/props'

import { getFieldIds } from './__utils__/getFieldIds'
import classes from './checkbox-field.module.scss'

export interface CheckboxProps extends ClassName {
  id: string
  isDisabled?: boolean
  label: ReactNode
  validators?: RegisterOptions
}

export const CheckboxField = ({
  className,
  id,
  isDisabled = false,
  label,
  validators
}: CheckboxProps) => {
  const {
    formState: { errors },
    register
  } = useFormContext()

  const error = errors[id]?.message
  const hasError = Boolean(error)
  const { describedBy, errorId, inputId } = getFieldIds(id)

  return (
    <div className={className}>
      <Label className={classes.label} htmlFor={id}>
        <div className={classes.wrapper}>
          <Input
            aria-describedby={describedBy}
            className={classes.input}
            hasError={hasError}
            id={inputId}
            isDisabled={isDisabled}
            type="checkbox"
            {...register(id, validators)}
          />
          <span className={classes.checkmark} />
        </div>
        <span className={classes.labelText}>{label}</span>
      </Label>
      {error && (
        <FieldError id={errorId} shouldHaveMarginTop={false}>
          {String(error)}
        </FieldError>
      )}
    </div>
  )
}

export interface CheckboxOnlyProps extends ClassName {
  id: string
  isDisabled?: boolean
  onClick?: (e: any) => void
  validators?: RegisterOptions
}

export const CheckboxOnlyField = ({
  className = '',
  id,
  isDisabled = false,
  onClick = noop,
  validators
}: CheckboxOnlyProps) => {
  const {
    formState: { errors },
    register
  } = useFormContext()

  const error = errors[id]?.message
  const hasError = Boolean(error)
  const { describedBy, errorId, inputId } = getFieldIds(id)

  const formProps = register(id, validators)

  return (
    <div className={clsx(classes.checkboxOnly, className)}>
      <Label className={classes.label} htmlFor={id}>
        <div className={classes.wrapper}>
          <Input
            aria-describedby={describedBy}
            className={classes.input}
            hasError={hasError}
            id={inputId}
            isDisabled={isDisabled}
            type="checkbox"
            {...formProps}
            onClick={e => {
              onClick(e)
              formProps.onChange(e)
            }}
          />
          <span className={classes.checkmark} />
        </div>
      </Label>
      {error && (
        <FieldError id={errorId} shouldHaveMarginTop={false}>
          {String(error)}
        </FieldError>
      )}
    </div>
  )
}
